import classNames from "classnames";
import FlickityFull from "flickity-fullscreen";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconExternalLink from "../../img/icons/external-link.svg";
import iconXClose from "../../img/icons/x-close.svg";
import { UGCListSubBlock } from "../models/ugc";
import { focusElement, trapFocus } from "../utils/keyboardFocus";
import { loadWistiaPlayer } from "../utils/wistia";
import { Image } from "./Image";
import { SocialIcon } from "./SocialIcon";
import { UGCRecommendations } from "./UGCRecommendations";

const MOBILE_WIDTH_THRESHOLD = 501;
const MEDIUM_SCREEN_WIDTH_THRESHOLD = 769;

const isMobileWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD;
};

const isMediumScreenWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MEDIUM_SCREEN_WIDTH_THRESHOLD;
};

const gotoGalleryLink = (
    event: React.MouseEvent<HTMLElement>,
    link?: string,
): void => {
    if (event) {
        event.preventDefault();
    }

    const window = getWindow();
    if (link && window) {
        window.open(link, "_blank");
    }
};

const isTilePhotoOrVideo = (tile: UGCListSubBlock): boolean => {
    return !!(
        tile.post &&
        (tile.post.post_type === "image" || tile.post.post_type === "video") &&
        tile.post.photo
    );
};

const isTextOnly = (tile: UGCListSubBlock): boolean => {
    return tile.post?.post_type === "text-only";
};

const getGalleryArrow = (isMobile: boolean) => {
    return isMobile
        ? {
              x0: 10,
              x1: 60,
              y1: 50,
              x2: 60,
              y2: 40,
              x3: 20,
          }
        : {
              x0: 35,
              x1: 110,
              y1: 120,
              x2: 115,
              y2: 120,
              x3: 40,
          };
};

const hasVideoTile = (tiles: UGCListSubBlock[]): boolean => {
    return tiles.some(
        (tile) =>
            tile.post?.post_type === "video" && tile.post?.video_link !== "",
    );
};

export const UGCGallery = (props: {
    tiles: UGCListSubBlock[];
    initialSlide: number;
    onCloseGallery: () => void;
}): React.ReactNode => {
    const [isMobile, setIsMobile] = React.useState(false);
    const [isMediumScreen, setIsMediumScreen] = React.useState(false);
    const [selectedSlide, setSelectedSlide] = React.useState(1);

    const galleryCarouselRef = React.useRef<HTMLDivElement>(null);
    const galleryFlktyRef = React.useRef<FlickityFull | null>(null);
    const { tiles, initialSlide, onCloseGallery } = props;
    const hasInitialVideoTile = React.useRef(hasVideoTile(tiles));

    // Required: Event listener and effect dependency needs stable reference for proper cleanup
    const handleCloseGallery = React.useCallback(() => {
        if (galleryFlktyRef.current) {
            galleryFlktyRef.current.exitFullscreen();
            galleryFlktyRef.current.destroy();
        }

        onCloseGallery();
    }, [onCloseGallery]);

    // Required: Event listener needs stable reference for proper cleanup
    const initializeGallery = React.useCallback(
        (mobile: boolean) => {
            if (galleryFlktyRef.current) {
                galleryFlktyRef.current.destroy();
            }

            const galleryArrow = getGalleryArrow(mobile);

            if (galleryCarouselRef.current) {
                const flkty = new FlickityFull(galleryCarouselRef.current, {
                    accessibility: true,
                    cellAlign: "center",
                    pageDots: false,
                    fullscreen: true,
                    cellSelector: ".ugc-carousel-gallery-tile",
                    arrowShape: galleryArrow,
                });

                galleryFlktyRef.current = flkty;

                flkty.select(initialSlide);
                flkty.on("select", (index) => {
                    setSelectedSlide(Number(index) + 1);
                });

                flkty.on("settle", () => {
                    const document = getDocument();
                    const activeModal = document?.querySelector<HTMLElement>(
                        ".ugc-carousel-gallery__content",
                    );
                    if (activeModal) {
                        trapFocus(activeModal);
                    }

                    const activeCloseButton =
                        document?.querySelector<HTMLButtonElement>(
                            ".ugc-carousel-gallery-tile.is-selected .ugc-carousel-gallery-tile__close",
                        );
                    if (activeCloseButton) {
                        // focus() is fired but it is not applied properly for some reason
                        // This hack makes focus() working properly
                        setTimeout(() => {
                            focusElement(activeCloseButton);
                        }, 0);
                    }
                    if (galleryFlktyRef.current) {
                        galleryFlktyRef.current.viewFullscreen();
                    }
                });

                flkty.on("dragStart.flickity", () => {
                    const document = getDocument();
                    if (document) {
                        document.ontouchmove = (event) => {
                            event.preventDefault();
                        };
                    }
                });

                flkty.on("dragEnd.flickity", () => {
                    const document = getDocument();
                    if (document) {
                        document.ontouchmove = () => {
                            return true;
                        };
                    }
                });

                flkty.on("fullscreenChange", (isFullscreen) => {
                    if (!isFullscreen && galleryFlktyRef.current) {
                        galleryFlktyRef.current.destroy();
                        onCloseGallery();
                    }

                    const document = getDocument();
                    const overlay = document?.querySelector<HTMLElement>(
                        ".flickity-enabled.is-fullscreen",
                    );
                    if (overlay) {
                        overlay.setAttribute("aria-modal", "true");
                        overlay.setAttribute(
                            "aria-label",
                            t`TempurPedic Social Media Posts`,
                        );
                        overlay.setAttribute("role", "dialog");
                    }

                    const nextBtn = document?.querySelector<HTMLElement>(
                        ".flickity-button.flickity-prev-next-button.next",
                    );
                    const prevBtn = document?.querySelector<HTMLElement>(
                        ".flickity-button.flickity-prev-next-button.previous",
                    );

                    if (nextBtn) {
                        nextBtn.setAttribute(
                            "aria-label",
                            t`View Next. Activating this element will cause content on the page to be updated.`,
                        );
                        nextBtn.classList.add("al-ugc-gallery-flickity--next");
                    }

                    if (prevBtn) {
                        prevBtn.setAttribute(
                            "aria-label",
                            t`View Previous. Activating this element will cause content on the page to be updated.`,
                        );
                        prevBtn.classList.add(
                            "al-ugc-gallery-flickity--previous",
                        );
                    }
                });

                flkty.on("pointerDown", (event) => {
                    if (mobile) {
                        return;
                    }

                    if (event.target) {
                        const target = event.target as HTMLElement;
                        if (
                            !target.classList.contains(
                                "ugc-carousel-gallery-tile",
                            )
                        ) {
                            return;
                        }
                        handleCloseGallery();
                    }
                });
            }
        },
        [handleCloseGallery, initialSlide, onCloseGallery],
    );

    // Handle resize and initialize gallery
    React.useEffect(() => {
        const window = getWindow();
        if (!window) return;

        const checkMobileState = () => {
            const mobile = isMobileWidth();
            const mediumScreen = isMediumScreenWidth();

            setIsMobile(mobile);
            setIsMediumScreen(mediumScreen);
            initializeGallery(mobile);
        };

        checkMobileState();
        window.addEventListener("resize", checkMobileState);

        return () => {
            window.removeEventListener("resize", checkMobileState);
        };
    }, [initializeGallery]);

    // Handle escape key
    React.useEffect(() => {
        const window = getWindow();
        if (!window) return;

        const handleGalleryEscapeKeyUp = (e: KeyboardEvent) => {
            const KEY_ESCAPE = "Escape";
            if (e.key === KEY_ESCAPE) {
                handleCloseGallery();
            }
        };

        window.addEventListener("keyup", handleGalleryEscapeKeyUp);

        return () => {
            window.removeEventListener("keyup", handleGalleryEscapeKeyUp);
        };
    }, [handleCloseGallery]);

    // Load Wistia player if needed
    React.useEffect(() => {
        if (hasInitialVideoTile.current) {
            loadWistiaPlayer();
        }
    }, []);

    // Cleanup gallery on unmount
    React.useEffect(() => {
        return () => {
            if (galleryFlktyRef.current) {
                galleryFlktyRef.current.destroy();
            }
        };
    }, []);

    const buildGalleryTiles = (): (React.JSX.Element | null)[] => {
        return tiles.map((tile, key) => {
            if (!tile.post) {
                return null;
            }
            const tileContentClasses = classNames({
                "ugc-carousel-gallery-tile__content": true,
                "ugc-carousel-gallery-tile__content--text-only":
                    isTextOnly(tile),
            });
            const tileDetailsClasses = classNames({
                "ugc-carousel-gallery-tile__details": true,
                "ugc-carousel-gallery-tile__details--text-only":
                    isTextOnly(tile),
            });
            const tileUsernameClasses = classNames({
                "ugc-carousel-gallery-tile__username": true,
                "ugc-carousel-gallery-tile__username--highlight":
                    tile.post.social_link,
            });
            const tileIconContainerClasses = classNames({
                "ugc-carousel-gallery-tile__icon-container": true,
                "ugc-carousel-gallery-tile__icon-container--empty":
                    tile.post.social_media === "none",
            });

            const imageVideo =
                tile.post.post_type === "video" && tile.post.video_link ? (
                    <span
                        className="ugc-carousel-gallery-tile__video al-ugc-carousel-gallery-tile--video"
                        dangerouslySetInnerHTML={{
                            __html: tile.post.video_link,
                        }}
                    ></span>
                ) : (
                    <Image
                        src={tile.post.photo}
                        className="ugc-carousel-gallery-tile__image"
                        alt={tile.post.photo_title}
                    />
                );
            const tilePhoto = isTilePhotoOrVideo(tile) ? (
                <div className="ugc-carousel-gallery-tile__image-container">
                    {imageVideo}
                </div>
            ) : null;
            const recommendations =
                tile.post.recommendations &&
                tile.post.recommendations.length > 0 ? (
                    <UGCRecommendations
                        recommendations={tile.post.recommendations}
                    />
                ) : null;

            const tileUsername = tile.post.social_link ? (
                <a
                    id={`post-${key}`}
                    className={tileUsernameClasses}
                    href={tile.post.social_link}
                    target="_blank"
                    rel="noreferrer"
                >
                    {tile.post.username}
                </a>
            ) : (
                <span className={tileUsernameClasses}>
                    {tile.post.username}
                </span>
            );

            const onTileShareClick = (e: React.MouseEvent<HTMLElement>) => {
                gotoGalleryLink(e, tile.post?.social_link);
            };
            const tileShare = tile.post.social_link ? (
                <button
                    aria-label={t`Open in Instagram`}
                    title={t`Open in Instagram`}
                    className="ugc-carousel-gallery-tile__link ugc-carousel-gallery-tile__share al-ugc-gallery__share-link"
                    onClick={onTileShareClick}
                >
                    <SVG
                        aria-hidden="true"
                        title={t`Open in Instagram`}
                        className="ugc-carousel-gallery-tile__close-icon al-ugc-gallery__close"
                        src={iconExternalLink}
                    />
                </button>
            ) : null;

            return (
                <li className="ugc-carousel-gallery-tile" key={key}>
                    <p className="ada-screenreader-only">
                        {t`To navigate, use the left and right arrows.`}
                    </p>
                    <section className={tileContentClasses}>
                        <div className={tileDetailsClasses}>
                            <div className="ugc-carousel-gallery-tile__attribution">
                                {tile.post.avatar && (
                                    <Image
                                        className="ugc-carousel-gallery-tile__avatar"
                                        src={tile.post.avatar}
                                        alt={tile.post.avatar_title}
                                    />
                                )}
                                <div className="ugc-carousel-gallery-tile__links">
                                    <button
                                        aria-label={t`Close`}
                                        title={t`Close Modal`}
                                        className="ugc-carousel-gallery-tile__link ugc-carousel-gallery-tile__close al-ugc-gallery__close"
                                        onClick={handleCloseGallery}
                                    >
                                        <SVG
                                            aria-hidden="true"
                                            title={t`Close`}
                                            className="ugc-carousel-gallery-tile__close-icon"
                                            src={iconXClose}
                                        />
                                    </button>
                                    {tileShare}
                                </div>
                                <h3 className="ugc-carousel-gallery-tile__title">
                                    <strong>{tile.post.title}</strong>
                                </h3>
                                <span className={tileIconContainerClasses}>
                                    <SocialIcon
                                        type={tile.post.social_media}
                                        className={
                                            "ugc-carousel-gallery-tile__icon"
                                        }
                                    />
                                </span>
                                {tileUsername}
                            </div>
                            <div className="ugc-carousel-gallery-tile__copy">
                                {!isMobile && tile.post.copy}
                                {!isMobile && isMediumScreen && recommendations}
                            </div>
                            {!isMobile && !isMediumScreen && recommendations}
                        </div>
                        <div className="ugc-carousel-gallery-tile__photo-and-copy">
                            {!isMobile && tilePhoto}
                            {isMobile && (
                                <div>
                                    {tilePhoto}
                                    <div className="ugc-carousel-gallery-tile__copy-container">
                                        <div className="ugc-carousel-gallery-tile__copy">
                                            {tile.post.copy}
                                            {recommendations}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </li>
            );
        });
    };

    return (
        <div className="ugc-carousel-gallery__content" ref={galleryCarouselRef}>
            {isMobile && (
                <span className="ugc-carousel-gallery__counter">
                    {selectedSlide} of {tiles.length}
                </span>
            )}
            <ul>{buildGalleryTiles()}</ul>
        </div>
    );
};
